import React from 'react';

/**
 * The rendered pixel width of `ref`, or 0 before it can be measured.
 *
 * recharts is drawn at an explicit `width` here rather than through `ResponsiveContainer`, which
 * measures 0 in jsdom and would render nothing in every test in this repo. The first attempt at a
 * fluid plot therefore CSS-stretched the surface to 100% and let the viewBox scale it — which draws
 * correctly but breaks POINTER MAPPING: recharts resolves the hovered index from the cursor's offset
 * against its own internal width, so a surface stretched past that width reports a neighbouring
 * point's reading. Measuring instead keeps one width in play for both drawing and hit-testing.
 *
 * The measurement is committed on the NEXT animation frame, never from inside the ResizeObserver
 * callback. A synchronous write there re-enters layout while the browser is still delivering that
 * batch of notifications, and this hook is the ONLY native ResizeObserver consumer in the app —
 * every notification measured during an Orbit open/close came from `orbit-chart`
 * (`browser-recheck13-20260915.md`), so it is the only code that can produce the
 * "ResizeObserver loop completed with undelivered notifications" the user saw. That report could
 * not be reproduced here, so this is the mechanism removed rather than a proven cause. Deferring by
 * a frame breaks the re-entry: only the last size seen before that frame is written, and an
 * unchanged rounded width is not written at all. PLA-8844.
 *
 * Returns 0 where ResizeObserver is unavailable (jsdom), so callers fall back to their given width
 * and every existing test renders exactly as before. PLA-8828.
 */
const useMeasuredWidth = (ref: React.RefObject<HTMLElement>): number => {
  const [width, setWidth] = React.useState(0);
  // What was last committed. React re-renders once before bailing out of an identical `useState`
  // value, so the comparison has to happen before the call, not inside the updater.
  const committed = React.useRef(0);

  React.useEffect(() => {
    const node = ref.current;

    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    let frame: number | null = null;

    const observer = new ResizeObserver(entries => {
      // Rounded because a fractional width makes recharts re-render on every sub-pixel reflow.
      const measured = Math.round(entries[entries.length - 1]?.contentRect?.width ?? 0);

      if (frame !== null) {
        cancelAnimationFrame(frame);
      }

      frame = requestAnimationFrame(() => {
        frame = null;

        if (committed.current === measured) {
          return;
        }

        committed.current = measured;
        setWidth(measured);
      });
    });

    observer.observe(node);

    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }

      observer.disconnect();
    };
  }, [ref]);

  return width;
};

export default useMeasuredWidth;
